import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { axiosInstance } from "@/App";
import { 
  Camera, 
  Mic, 
  MicOff, 
  Video, 
  VideoOff, 
  Wifi, 
  WifiOff,
  Radio,
  SwitchCamera,
  AlertCircle,
  Moon
} from "lucide-react";
import { toast } from "sonner";
import {
  LiveKitRoom,
  useLocalParticipant,
  useRoomContext,
  useTracks,
  VideoTrack,
} from "@livekit/components-react";
import "@livekit/components-styles";
import { Track, RoomEvent, VideoQuality } from "livekit-client";
import { Button } from "@/components/ui/button";
import { useResilientWebSocket } from "@/hooks/useResilientWebSocket";
import { useLiveViewportLock } from "@/components/LiveExperienceChrome";
import StreamDiagnostics from "@/components/StreamDiagnostics";
import {
  getBrowserProVideoProfile,
  getProAudioCaptureOptions,
  getProAudioPublishOptions,
  getProConnectionReadiness,
  getProPublishDefaults,
  getProTrackOrientation,
  getProVideoOptionCandidates,
  getProVideoOptions,
  normalizeProAudioMode,
  PRO_AUDIO_MODE_RAW,
} from "@/utils/proModeMedia";
import {
  clampZoom,
  getNextSmoothZoom,
  SMOOTH_ZOOM_FRAME_MS,
} from "@/utils/smoothZoom";
import { STREAM_ORIENTATION } from "@/utils/eventCreation";

const PRO_MODE_COMMAND_TOPIC = 'showme-pro-control';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const FULL_BLEED_VIDEO_STYLE = {
  height: '100%',
  width: '100%',
  objectFit: 'cover',
  objectPosition: 'center',
};
const TRACK_FORMAT_SETTLE_MS = 160;

const waitForTrackFormat = () => new Promise(
  (resolve) => window.setTimeout(resolve, TRACK_FORMAT_SETTLE_MS)
);

const getManagedCameraTrack = (localParticipant) => {
  const publication = localParticipant?.getTrackPublication(Track.Source.Camera);
  return publication?.videoTrack || publication?.track || null;
};

const logCameraFormat = ({ deviceNumber, orientation, facingMode, reason, mediaTrack }) => {
  const settings = mediaTrack?.getSettings?.() || {};
  console.info('[CamDevice] camera format checked', {
    deviceNumber,
    reason,
    expectedOrientation: orientation === 'landscape' ? 'landscape' : 'portrait',
    actualOrientation: getProTrackOrientation(settings),
    facingMode,
    readyState: mediaTrack?.readyState,
    settings,
  });
};

const enforceProCameraOrientation = async ({
  localVideoTrack,
  orientation,
  facingMode,
  deviceNumber,
  reason,
  forceRestart = false,
}) => {
  if (!localVideoTrack?.restartTrack) {
    throw new Error('No managed camera track is available');
  }

  let mediaTrack = localVideoTrack.mediaStreamTrack;
  if (
    !forceRestart
    && mediaTrack?.readyState === 'live'
  ) {
    logCameraFormat({ deviceNumber, orientation, facingMode, reason, mediaTrack });
    return mediaTrack;
  }

  // A managed LiveKit track must be restarted at most once per actual camera
  // lifecycle event. Repeated format probes replace the underlying iOS track
  // several times and can leave its RTCRtpSender frozen or black.
  await localVideoTrack.restartTrack(getProVideoOptions(orientation, facingMode));
  await waitForTrackFormat();
  mediaTrack = localVideoTrack.mediaStreamTrack;
  logCameraFormat({ deviceNumber, orientation, facingMode, reason, mediaTrack });
  if (!mediaTrack || mediaTrack.readyState !== 'live') {
    throw new Error('The replacement camera track did not become live');
  }

  const actualOrientation = getProTrackOrientation(mediaTrack);
  const expectedOrientation = orientation === 'landscape' ? 'landscape' : 'portrait';
  if (actualOrientation !== 'unknown' && actualOrientation !== expectedOrientation) {
    console.warn('[CamDevice] browser reported a different camera orientation', {
      deviceNumber,
      reason,
      expectedOrientation,
      actualOrientation,
    });
  }
  return mediaTrack;
};

const enableProCamera = async ({
  localParticipant,
  orientation,
  facingMode,
  deviceNumber,
  reason,
}) => {
  let localVideoTrack = getManagedCameraTrack(localParticipant);
  if (!localVideoTrack) {
    let lastError = null;
    const candidates = getProVideoOptionCandidates(orientation, facingMode);
    for (const [index, videoOptions] of candidates.entries()) {
      try {
        await localParticipant.setCameraEnabled(true, videoOptions);
        localVideoTrack = getManagedCameraTrack(localParticipant);
        if (localVideoTrack) break;
      } catch (candidateError) {
        lastError = candidateError;
        console.warn('[CamDevice] camera enable preset unavailable', {
          deviceNumber,
          reason,
          preset: index + 1,
          orientation,
          facingMode,
          error: String(candidateError),
        });
      }
    }
    if (!localVideoTrack) {
      throw lastError || new Error('Unable to publish the camera');
    }
  }

  return enforceProCameraOrientation({
    localVideoTrack,
    orientation,
    facingMode,
    deviceNumber,
    reason,
  });
};

// Camera Publisher Component
const CameraPublisher = ({
  eventId, deviceNumber, isActive, onActiveChange,
  onControlPanelPresenceChange, onStatusChange, orientation,
  audioMode, onAudioModeChange, videoProfile,
}) => {
  const { localParticipant } = useLocalParticipant();
  const room = useRoomContext();
  const tracks = useTracks([Track.Source.Camera, Track.Source.Microphone]);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [micEnabledByControl, setMicEnabledByControl] = useState(true);
  const [focusPulse, setFocusPulse] = useState(false);
  const cameraEnabledRef = useRef(true);
  const facingModeRef = useRef('user');
  const switchInProgressRef = useRef(false);
  const recoveryInProgressRef = useRef(false);
  const mutedWatchdogPollsRef = useRef(0);
  const focusPulseTimerRef = useRef(null);
  const activeSwitchTimerRef = useRef(null);
  
  const cameraTrack = tracks.find(
    (t) => t.participant.isLocal && t.source === Track.Source.Camera
  );

  const scheduleActiveSwitch = useCallback((active, delayMs = 0) => {
    if (activeSwitchTimerRef.current) {
      clearTimeout(activeSwitchTimerRef.current);
      activeSwitchTimerRef.current = null;
    }
    const applySwitch = () => {
      activeSwitchTimerRef.current = null;
      setMicEnabledByControl(active);
      onActiveChange?.(active);
      if (active) toast.success('You are now LIVE!', { duration: 3000 });
    };
    const safeDelay = Math.max(0, Number(delayMs) || 0);
    if (safeDelay > 0) {
      activeSwitchTimerRef.current = setTimeout(applySwitch, safeDelay);
    } else {
      applySwitch();
    }
  }, [onActiveChange]);

  useEffect(() => () => {
    if (activeSwitchTimerRef.current) clearTimeout(activeSwitchTimerRef.current);
  }, []);

  // Sync microphone based on active status
  useEffect(() => {
    const syncMic = async () => {
      if (localParticipant) {
        try {
          // Exactly one camera microphone may publish: the device currently
          // selected by the Control Panel, unless its mic was manually muted.
          await localParticipant.setMicrophoneEnabled(
            isActive && micEnabledByControl,
            getProAudioCaptureOptions(audioMode),
            getProAudioPublishOptions(audioMode),
          );
        } catch (error) {
          console.error("Error syncing mic:", error);
        }
      }
    };
    syncMic();
  }, [audioMode, localParticipant, isActive, micEnabledByControl]);

  // Adapt the published simulcast ceiling when LiveKit reports sustained
  // network trouble. This changes encoder layers only; it never reacquires the
  // camera, preserving the framing and reliable mobile connection lifecycle.
  useEffect(() => {
    if (!room || !localParticipant) return undefined;
    let lastAppliedQuality = null;
    const applyNetworkQuality = (quality, participant) => {
      if (participant?.identity && participant.identity !== localParticipant.identity) return;
      const normalized = String(quality || localParticipant.connectionQuality || '').toLowerCase();
      const constrained = normalized.includes('poor') || normalized.includes('lost');
      const nextQuality = constrained ? VideoQuality.MEDIUM : VideoQuality.HIGH;
      if (lastAppliedQuality === nextQuality) return;
      const localVideoTrack = getManagedCameraTrack(localParticipant);
      localVideoTrack?.setPublishingQuality?.(nextQuality);
      lastAppliedQuality = nextQuality;
      onStatusChange?.(constrained ? 'network_limited' : 'network_ready');
    };
    applyNetworkQuality(localParticipant.connectionQuality, localParticipant);
    room.on(RoomEvent.ConnectionQualityChanged, applyNetworkQuality);
    return () => room.off(RoomEvent.ConnectionQualityChanged, applyNetworkQuality);
  }, [localParticipant, onStatusChange, room]);

  // Publish in the camera's native format. Existing tracks are left alone;
  // restartTrack is reserved for camera recovery and manual lens switching.
  useEffect(() => {
    const enableCamera = async () => {
      if (localParticipant) {
        try {
          await enableProCamera({
            localParticipant,
            orientation,
            facingMode: 'user',
            deviceNumber,
            reason: 'initial-enable',
          });
          cameraEnabledRef.current = true;
          facingModeRef.current = 'user';
          setCameraEnabled(true);
          onStatusChange?.('camera_enabled');
        } catch (error) {
          console.error("Error enabling camera:", error);
          onStatusChange?.('camera_error');
        }
      }
    };
    enableCamera();
  }, [deviceNumber, localParticipant, orientation, onStatusChange]);

  // Recover an ended or persistently muted track. Calling
  // setCameraEnabled(true) alone does not repair an enabled publication whose
  // underlying browser track has already stopped.
  useEffect(() => {
    if (!localParticipant) return;
    let cancelled = false;

    const recoverCamera = async (reason = 'watchdog') => {
      if (
        cancelled
        || !cameraEnabledRef.current
        || switchInProgressRef.current
        || recoveryInProgressRef.current
      ) return;

      const publication = localParticipant.getTrackPublication(Track.Source.Camera);
      const localVideoTrack = publication?.videoTrack || publication?.track;
      const mediaTrack = localVideoTrack?.mediaStreamTrack;
      const ended = !publication || !localVideoTrack || !mediaTrack || mediaTrack.readyState === 'ended';

      if (mediaTrack?.muted) mutedWatchdogPollsRef.current += 1;
      else mutedWatchdogPollsRef.current = 0;

      const stalled = mutedWatchdogPollsRef.current >= 2;
      if (!ended && !stalled) return;

      recoveryInProgressRef.current = true;
      console.warn('[CamDevice] recovering camera track', {
        deviceNumber,
        reason,
        ended,
        stalled,
        facingMode: facingModeRef.current,
      });

      try {
        if (!localVideoTrack) throw new Error('No managed camera track is published');
        await enforceProCameraOrientation({
          localVideoTrack,
          orientation,
          facingMode: facingModeRef.current,
          deviceNumber,
          reason,
          forceRestart: true,
        });
        mutedWatchdogPollsRef.current = 0;
        console.info('[CamDevice] camera track recovered', { deviceNumber, reason });
      } catch (restartError) {
        console.warn('[CamDevice] managed restart failed; republishing camera:', restartError);
        try {
          await localParticipant.setCameraEnabled(false);
          await new Promise((resolve) => window.setTimeout(resolve, 180));
          await enableProCamera({
            localParticipant,
            orientation,
            facingMode: facingModeRef.current,
            deviceNumber,
            reason: `${reason}:republish`,
          });
          mutedWatchdogPollsRef.current = 0;
          console.info('[CamDevice] camera track republished', { deviceNumber, reason });
        } catch (republishError) {
          console.error('[CamDevice] camera recovery failed:', republishError);
          onStatusChange?.('camera_error');
        }
      } finally {
        recoveryInProgressRef.current = false;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') recoverCamera('page_visible');
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);
    const poll = setInterval(() => recoverCamera('watchdog'), 4000);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
      clearInterval(poll);
    };
  }, [deviceNumber, localParticipant, onStatusChange, orientation]);

  const toggleCamera = useCallback(async () => {
    if (!localParticipant || switchInProgressRef.current) return;

    const previousFacing = facingModeRef.current;
    const newFacing = facingModeRef.current === 'user' ? 'environment' : 'user';
    switchInProgressRef.current = true;
    onStatusChange?.('camera_switching');
    console.info('[CamDevice] camera switch started', { deviceNumber, newFacing, orientation });

    try {
      const cameraPublication = localParticipant.getTrackPublication(Track.Source.Camera);
      const localVideoTrack = cameraPublication?.videoTrack || cameraPublication?.track;
      if (!localVideoTrack?.restartTrack) throw new Error('No managed camera track is published');

      // LiveKit owns the MediaStreamTrack lifecycle. Keeping the same managed
      // LocalVideoTrack preserves its RTCRtpSender and remote publication while
      // the phone changes from front to back (or back to front).
      const switchedMediaTrack = await enforceProCameraOrientation({
        localVideoTrack,
        orientation,
        facingMode: newFacing,
        deviceNumber,
        reason: 'front-back-switch',
        forceRestart: true,
      });

      facingModeRef.current = newFacing;
      mutedWatchdogPollsRef.current = 0;
      onStatusChange?.('camera_enabled');
      console.info('[CamDevice] camera switch completed', {
        deviceNumber,
        newFacing,
        settings: switchedMediaTrack.getSettings?.(),
      });
      toast.success(`Switched to ${newFacing === 'user' ? 'front' : 'back'} camera`);
    } catch (restartError) {
      console.warn('[CamDevice] orientation-safe camera switch failed; restoring the previous camera:', restartError);
      try {
        const cameraPublication = localParticipant.getTrackPublication(Track.Source.Camera);
        const localVideoTrack = cameraPublication?.videoTrack || cameraPublication?.track;
        if (!localVideoTrack?.restartTrack) throw new Error('No managed camera track is published');
        await enforceProCameraOrientation({
          localVideoTrack,
          orientation,
          facingMode: previousFacing,
          deviceNumber,
          reason: 'front-back-switch-rollback',
          forceRestart: true,
        });
        facingModeRef.current = previousFacing;
        mutedWatchdogPollsRef.current = 0;
        onStatusChange?.('camera_enabled');
      } catch (rollbackError) {
        console.error('[CamDevice] camera switch rollback failed:', rollbackError);
        onStatusChange?.('camera_error');
      }
      toast.error('Unable to switch cameras on this device');
    } finally {
      switchInProgressRef.current = false;
    }
  }, [deviceNumber, localParticipant, onStatusChange, orientation]);

  // Receive Control Panel commands over LiveKit's reliable data channel. This
  // uses the same production connection as the camera track and avoids a
  // second WebSocket that is not supported by every serverless host.
  useEffect(() => {
    if (!room) return undefined;

    const handleData = (payload, participant, _kind, topic) => {
      if (topic !== PRO_MODE_COMMAND_TOPIC || participant?.identity !== 'control-panel') return;
      try {
        const data = JSON.parse(new TextDecoder().decode(payload));
        const ownDeviceId = `${eventId}-device-${deviceNumber}`;
        if (data.device_id && data.device_id !== ownDeviceId) return;

        if (data.type === 'device_switch') {
          const nowActive = data.active_device_id === ownDeviceId;
          scheduleActiveSwitch(nowActive, data.audio_delay_ms);
        } else if (data.type === 'go_live') {
          toast.success('Stream started!');
        } else if (data.type === 'switch_camera') {
          window.dispatchEvent(new CustomEvent('pro-mode-switch-camera'));
        } else if (data.type === 'set_camera_enabled') {
          window.dispatchEvent(new CustomEvent('pro-mode-set-camera-enabled', {
            detail: { enabled: data.enabled !== false }
          }));
        } else if (data.type === 'ensure_camera_published') {
          window.dispatchEvent(new CustomEvent('pro-mode-ensure-camera-published'));
        } else if (data.type === 'focus_camera') {
          window.dispatchEvent(new CustomEvent('pro-mode-focus-camera'));
        } else if (data.type === 'set_zoom') {
          window.dispatchEvent(new CustomEvent('pro-mode-set-zoom', { detail: { zoom: data.zoom } }));
        } else if (data.type === 'set_torch') {
          window.dispatchEvent(new CustomEvent('pro-mode-set-torch', { detail: { enabled: data.enabled === true } }));
        } else if (data.type === 'audio_settings_update') {
          if (typeof data.settings?.mic_enabled === 'boolean') {
            setMicEnabledByControl(data.settings.mic_enabled);
          }
          if (data.settings?.mode) onAudioModeChange?.(normalizeProAudioMode(data.settings.mode));
        } else if (data.type === 'end_stream') {
          window.dispatchEvent(new CustomEvent('pro-mode-end-stream'));
          toast.info('Stream ended by control panel');
        }
      } catch (error) {
        console.warn('[CamDevice] ignored invalid control message:', error);
      }
    };

    room.on(RoomEvent.DataReceived, handleData);
    return () => room.off(RoomEvent.DataReceived, handleData);
  }, [deviceNumber, eventId, onAudioModeChange, room, scheduleActiveSwitch]);

  useEffect(() => {
    const ensureCameraPublished = async () => {
      if (
        !localParticipant
        || !cameraEnabledRef.current
        || switchInProgressRef.current
        || recoveryInProgressRef.current
      ) return;

      recoveryInProgressRef.current = true;
      try {
        const publication = localParticipant.getTrackPublication(Track.Source.Camera);
        const localVideoTrack = publication?.videoTrack || publication?.track;
        const mediaTrack = localVideoTrack?.mediaStreamTrack;
        const healthy = Boolean(localVideoTrack && mediaTrack?.readyState === 'live' && !mediaTrack.muted);
        if (healthy) {
          // The publisher is healthy but the control panel lost the remote
          // publication. Republish the same managed track without reacquiring
          // the camera or changing its framing.
          await localParticipant.unpublishTrack(localVideoTrack, false);
          await localParticipant.publishTrack(
            localVideoTrack,
            getProPublishDefaults(videoProfile, audioMode),
          );
        } else {
          await localParticipant.setCameraEnabled(false);
          await new Promise((resolve) => window.setTimeout(resolve, 180));
          await enableProCamera({
            localParticipant,
            orientation,
            facingMode: facingModeRef.current,
            deviceNumber,
            reason: 'control-panel-republish',
          });
        }
        mutedWatchdogPollsRef.current = 0;
        onStatusChange?.('camera_enabled');
        console.info('[CamDevice] camera publication restored', { deviceNumber });
      } catch (republishError) {
        console.warn('[CamDevice] control-panel republish failed', republishError);
        onStatusChange?.('camera_error');
      } finally {
        recoveryInProgressRef.current = false;
      }
    };

    window.addEventListener('pro-mode-ensure-camera-published', ensureCameraPublished);
    return () => window.removeEventListener('pro-mode-ensure-camera-published', ensureCameraPublished);
  }, [audioMode, deviceNumber, localParticipant, onStatusChange, orientation, videoProfile]);

  useEffect(() => {
    const handleDeviceActive = (event) => {
      const active = event.detail?.active === true;
      scheduleActiveSwitch(active, event.detail?.delayMs);
    };
    const handleAudioSettings = (event) => {
      if (typeof event.detail?.settings?.mic_enabled === 'boolean') {
        setMicEnabledByControl(event.detail.settings.mic_enabled);
      }
      if (event.detail?.settings?.mode) {
        onAudioModeChange?.(normalizeProAudioMode(event.detail.settings.mode));
      }
    };
    window.addEventListener('pro-mode-device-active', handleDeviceActive);
    window.addEventListener('pro-mode-audio-settings', handleAudioSettings);
    return () => {
      window.removeEventListener('pro-mode-device-active', handleDeviceActive);
      window.removeEventListener('pro-mode-audio-settings', handleAudioSettings);
    };
  }, [onAudioModeChange, scheduleActiveSwitch]);

  useEffect(() => {
    if (!room) return undefined;
    const updatePresence = () => {
      onControlPanelPresenceChange?.(room.remoteParticipants.has('control-panel'));
    };
    updatePresence();
    room.on(RoomEvent.ParticipantConnected, updatePresence);
    room.on(RoomEvent.ParticipantDisconnected, updatePresence);
    return () => {
      room.off(RoomEvent.ParticipantConnected, updatePresence);
      room.off(RoomEvent.ParticipantDisconnected, updatePresence);
      onControlPanelPresenceChange?.(false);
    };
  }, [onControlPanelPresenceChange, room]);

  // Listen for remote switch_camera command from control panel
  useEffect(() => {
    const handler = () => toggleCamera();
    window.addEventListener('pro-mode-switch-camera', handler);
    return () => window.removeEventListener('pro-mode-switch-camera', handler);
  }, [toggleCamera]);

  useEffect(() => {
    const handler = async (event) => {
      const enabled = event.detail?.enabled !== false;
      cameraEnabledRef.current = enabled;
      try {
        if (enabled) {
          await enableProCamera({
            localParticipant,
            orientation,
            facingMode: facingModeRef.current,
            deviceNumber,
            reason: 'control-panel-enable',
          });
        } else {
          await localParticipant?.setCameraEnabled(false);
        }
        setCameraEnabled(enabled);
        toast.info(enabled ? 'Camera enabled by Control Panel' : 'Camera paused by Control Panel');
      } catch {
        toast.error('Unable to update camera state');
      }
    };
    window.addEventListener('pro-mode-set-camera-enabled', handler);
    return () => window.removeEventListener('pro-mode-set-camera-enabled', handler);
  }, [deviceNumber, localParticipant, orientation]);

  useEffect(() => {
    const handler = async () => {
      const publication = localParticipant?.getTrackPublication(Track.Source.Camera);
      const localVideoTrack = publication?.videoTrack || publication?.track;
      const mediaTrack = localVideoTrack?.mediaStreamTrack;
      if (!mediaTrack) return;
      setFocusPulse(true);
      window.clearTimeout(focusPulseTimerRef.current);
      focusPulseTimerRef.current = window.setTimeout(() => setFocusPulse(false), 850);
      try {
        const capabilities = mediaTrack.getCapabilities?.() || {};
        const focusModes = Array.isArray(capabilities.focusMode) ? capabilities.focusMode : [];
        const advanced = {};
        if (focusModes.includes('single-shot')) advanced.focusMode = 'single-shot';
        else if (focusModes.includes('continuous')) advanced.focusMode = 'continuous';
        if (capabilities.pointsOfInterest) advanced.pointsOfInterest = [{ x: 0.5, y: 0.5 }];

        if (advanced.focusMode || advanced.pointsOfInterest) {
          await mediaTrack.applyConstraints({ advanced: [advanced] });
          if (focusModes.includes('continuous') && advanced.focusMode !== 'continuous') {
            await new Promise((resolve) => window.setTimeout(resolve, 220));
            await mediaTrack.applyConstraints({ advanced: [{ focusMode: 'continuous' }] });
          }
          toast.success('ShowMeFocus applied');
          return;
        }
      } catch (focusError) {
        console.warn('[CamDevice] native focus constraint failed:', focusError);
      }

      // Safari manages continuous autofocus but does not expose focusMode.
      // Restarting the track here caused a visible stream interruption, so the
      // safe fallback intentionally leaves the working publication untouched.
      toast.info('Continuous autofocus is managed by this camera');
    };
    window.addEventListener('pro-mode-focus-camera', handler);
    return () => {
      window.removeEventListener('pro-mode-focus-camera', handler);
      window.clearTimeout(focusPulseTimerRef.current);
    };
  }, [localParticipant]);

  useEffect(() => {
    const smoothZoom = {
      timer: null,
      track: null,
      current: null,
      target: null,
      min: 1,
      max: 1,
      step: 0.01,
      unsupportedNotified: false,
    };
    let disposed = false;

    const scheduleZoomFrame = () => {
      if (disposed || smoothZoom.timer) return;
      smoothZoom.timer = window.setTimeout(applyZoomFrame, SMOOTH_ZOOM_FRAME_MS);
    };

    const applyZoomFrame = async () => {
      smoothZoom.timer = null;
      if (disposed || !smoothZoom.track || smoothZoom.target === null) return;
      const nextZoom = getNextSmoothZoom({
        current: smoothZoom.current,
        target: smoothZoom.target,
        min: smoothZoom.min,
        max: smoothZoom.max,
        step: smoothZoom.step,
      });
      try {
        await smoothZoom.track.applyConstraints({ advanced: [{ zoom: nextZoom }] });
        smoothZoom.current = nextZoom;
        if (Math.abs(smoothZoom.target - smoothZoom.current) > 0.0001) {
          scheduleZoomFrame();
        }
      } catch (zoomError) {
        smoothZoom.target = smoothZoom.current;
        console.warn('[CamDevice] smooth zoom constraint unavailable:', zoomError);
      }
    };

    const handleZoom = (event) => {
      const publication = localParticipant?.getTrackPublication(Track.Source.Camera);
      const mediaTrack = (publication?.videoTrack || publication?.track)?.mediaStreamTrack;
      if (!mediaTrack) return;
      const requestedZoom = Number(event.detail?.zoom);
      try {
        const capabilities = mediaTrack.getCapabilities?.() || {};
        const zoom = capabilities.zoom;
        if (!zoom || !Number.isFinite(requestedZoom)) throw new Error('Zoom is not supported');
        const settingsZoom = Number(mediaTrack.getSettings?.().zoom);
        if (smoothZoom.track !== mediaTrack || smoothZoom.current === null) {
          smoothZoom.track = mediaTrack;
          smoothZoom.min = Number(zoom.min);
          smoothZoom.max = Number(zoom.max);
          smoothZoom.step = Number(zoom.step) > 0 ? Number(zoom.step) : 0.01;
          smoothZoom.current = Number.isFinite(settingsZoom)
            ? clampZoom(settingsZoom, smoothZoom.min, smoothZoom.max)
            : smoothZoom.min;
        }
        smoothZoom.target = clampZoom(requestedZoom, smoothZoom.min, smoothZoom.max);
        smoothZoom.unsupportedNotified = false;
        scheduleZoomFrame();
      } catch (zoomError) {
        console.warn('[CamDevice] zoom command unavailable:', zoomError);
        if (!smoothZoom.unsupportedNotified) {
          smoothZoom.unsupportedNotified = true;
          toast.info('Zoom is not supported by this camera');
        }
      }
    };
    const handleTorch = async (event) => {
      const publication = localParticipant?.getTrackPublication(Track.Source.Camera);
      const mediaTrack = (publication?.videoTrack || publication?.track)?.mediaStreamTrack;
      if (!mediaTrack) return;
      try {
        const capabilities = mediaTrack.getCapabilities?.() || {};
        if (!capabilities.torch) throw new Error('Torch is not supported');
        await mediaTrack.applyConstraints({ advanced: [{ torch: event.detail?.enabled === true }] });
      } catch (torchError) {
        console.warn('[CamDevice] torch command unavailable:', torchError);
        toast.info('Back light is not supported by this camera');
      }
    };
    window.addEventListener('pro-mode-set-zoom', handleZoom);
    window.addEventListener('pro-mode-set-torch', handleTorch);
    return () => {
      disposed = true;
      window.clearTimeout(smoothZoom.timer);
      window.removeEventListener('pro-mode-set-zoom', handleZoom);
      window.removeEventListener('pro-mode-set-torch', handleTorch);
    };
  }, [localParticipant]);

  // Listen for end_stream command — stop publishing
  useEffect(() => {
    const handler = async () => {
      if (localParticipant) {
        try {
          cameraEnabledRef.current = false;
          setCameraEnabled(false);
          await localParticipant.setCameraEnabled(false);
          await localParticipant.setMicrophoneEnabled(false);
        } catch {}
      }
    };
    window.addEventListener('pro-mode-end-stream', handler);
    return () => window.removeEventListener('pro-mode-end-stream', handler);
  }, [localParticipant]);

  const portrait = orientation !== 'landscape';

  return (
    <div
      className="relative h-full w-full overflow-hidden bg-black"
      data-video-orientation={portrait ? 'portrait' : 'landscape'}
    >
      {cameraEnabled && cameraTrack ? (
        <VideoTrack 
          trackRef={cameraTrack} 
          className="absolute inset-0 !h-full !w-full !object-cover"
          style={FULL_BLEED_VIDEO_STYLE}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <Camera className="w-16 h-16 text-gray-600 mx-auto mb-2" />
            <p className="text-gray-400">{cameraEnabled ? 'Starting camera...' : 'Camera paused by Control Panel'}</p>
          </div>
        </div>
      )}

      {focusPulse && (
        <div className="pointer-events-none absolute left-1/2 top-1/2 z-30 h-24 w-24 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-2xl border-2 border-blue-400 shadow-[0_0_28px_rgba(59,130,246,.7)]" />
      )}

      {/* Camera Controls Overlay */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-3">
        <button
          onClick={toggleCamera}
          disabled={!cameraEnabled}
          className="w-14 h-14 bg-gray-800/80 hover:bg-gray-700 rounded-full flex items-center justify-center text-white backdrop-blur-sm disabled:cursor-not-allowed disabled:opacity-40"
        >
          <SwitchCamera className="w-6 h-6" />
        </button>
      </div>

      {/* Status Overlay */}
      <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-gray-900/80 backdrop-blur-sm px-3 py-1.5 rounded-lg flex items-center gap-2">
            <Camera className="w-4 h-4 text-blue-400" />
            <span className="text-white text-sm font-bold">CAM {deviceNumber}</span>
          </div>
          
          {isActive && (
            <div className="bg-red-600 px-3 py-1.5 rounded-lg flex items-center gap-2 animate-pulse">
              <Radio className="w-4 h-4 text-white" />
              <span className="text-white text-sm font-bold">LIVE</span>
            </div>
          )}
        </div>

        <div className={`px-3 py-1.5 rounded-lg flex items-center gap-2 ${isActive && micEnabledByControl ? 'bg-green-600' : 'bg-gray-700'}`}>
          {isActive && micEnabledByControl ? (
            <>
              <Mic className="w-4 h-4 text-white" />
              <span className="text-white text-xs">MIC ON</span>
            </>
          ) : (
            <>
              <MicOff className="w-4 h-4 text-white" />
              <span className="text-white text-xs">MIC OFF</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Camera Device Page
const ProModeCameraDevice = ({ user }) => {
  const { eventId, deviceNumber } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [event, setEvent] = useState(null);
  const [livekitToken, setLivekitToken] = useState(null);
  const [livekitUrl, setLivekitUrl] = useState(null);
  const [liveKitConnected, setLiveKitConnected] = useState(false);
  const [controlPanelPresent, setControlPanelPresent] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mediaPermissionState, setMediaPermissionState] = useState('idle');
  const [mediaPermissionError, setMediaPermissionError] = useState('');
  const [streamPrepConfirmed, setStreamPrepConfirmed] = useState(false);
  const liveViewport = useLiveViewportLock({
    active: streamPrepConfirmed && !loading && !error,
    allowRotation: true,
  });
  const [liveKitRoomGeneration, setLiveKitRoomGeneration] = useState(0);
  const [audioMode, setAudioMode] = useState(PRO_AUDIO_MODE_RAW);
  const [videoProfile] = useState(() => getBrowserProVideoProfile());
  const mediaPermissionAttemptedRef = useRef(false);
  const wakeLockRef = useRef(null);
  const liveKitRecoveryTimerRef = useRef(null);
  const liveKitRecoveryAttemptRef = useRef(0);
  
  const deviceNum = parseInt(deviceNumber);
  const connectionToken = searchParams.get("token");
  const isConnected = liveKitConnected && controlPanelPresent;
  const connectionReadiness = getProConnectionReadiness(
    videoProfile,
    typeof navigator === 'undefined'
      ? {}
      : (navigator.connection || navigator.mozConnection || navigator.webkitConnection || {}),
  );

  const cancelLiveKitRecovery = useCallback(() => {
    window.clearTimeout(liveKitRecoveryTimerRef.current);
    liveKitRecoveryTimerRef.current = null;
  }, []);

  const scheduleLiveKitRecovery = useCallback(() => {
    if (liveKitRecoveryTimerRef.current || !connectionToken || !eventId || !deviceNum) return;

    const attemptRecovery = async () => {
      liveKitRecoveryTimerRef.current = null;
      if (!navigator.onLine) {
        liveKitRecoveryTimerRef.current = window.setTimeout(attemptRecovery, 2000);
        return;
      }
      try {
        const registerRes = await axiosInstance.post("/pro-mode/device/register-public", {
          event_id: eventId,
          device_number: deviceNum,
          device_name: `Camera ${deviceNum}`,
          connection_token: connectionToken,
        });
        setLivekitToken(registerRes.data.livekit_token);
        setLivekitUrl(registerRes.data.livekit_url);
        setAudioMode(normalizeProAudioMode(registerRes.data.device?.audio_settings?.mode));
        setLiveKitRoomGeneration((generation) => generation + 1);
        liveKitRecoveryAttemptRef.current += 1;
        console.info('[CamDevice] LiveKit room recovery started', {
          deviceNumber: deviceNum,
          attempt: liveKitRecoveryAttemptRef.current,
        });
      } catch (recoveryError) {
        liveKitRecoveryAttemptRef.current += 1;
        const retryDelay = Math.min(10000, 1000 * (2 ** liveKitRecoveryAttemptRef.current));
        console.warn('[CamDevice] LiveKit recovery request failed; retrying', {
          deviceNumber: deviceNum,
          retryDelay,
          error: String(recoveryError),
        });
        liveKitRecoveryTimerRef.current = window.setTimeout(attemptRecovery, retryDelay);
      }
    };

    const initialDelay = Math.min(5000, 1000 * (2 ** liveKitRecoveryAttemptRef.current));
    liveKitRecoveryTimerRef.current = window.setTimeout(attemptRecovery, initialDelay);
  }, [connectionToken, deviceNum, eventId]);

  useEffect(() => () => cancelLiveKitRecovery(), [cancelLiveKitRecovery]);

  const requestCameraAndMic = useCallback(async () => {
    if (mediaPermissionState === 'requesting') return;
    if (!navigator.mediaDevices?.getUserMedia) {
      setMediaPermissionState('denied');
      setMediaPermissionError('This browser cannot open the camera and microphone. Open the QR link in Safari or Chrome over HTTPS.');
      return;
    }
    setMediaPermissionState('requesting');
    setMediaPermissionError('');
    try {
      const permissionStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'user' },
        },
        audio: getProAudioCaptureOptions(audioMode),
      });
      permissionStream.getTracks().forEach((track) => track.stop());
      setMediaPermissionState('granted');
    } catch (permissionError) {
      console.warn('[CamDevice] combined camera/microphone permission unavailable:', permissionError);
      setMediaPermissionState('denied');
      setMediaPermissionError(
        'Camera and microphone access are both required. Tap below, then allow both permissions in your browser.'
      );
    }
  }, [audioMode, mediaPermissionState]);

  const requestScreenWakeLock = useCallback(async () => {
    if (!navigator.wakeLock?.request || document.visibilityState !== 'visible') return;
    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen');
      wakeLockRef.current.addEventListener?.('release', () => {
        wakeLockRef.current = null;
      }, { once: true });
    } catch (wakeLockError) {
      console.info('[CamDevice] screen wake lock is unavailable:', wakeLockError);
    }
  }, []);

  const confirmStreamPrep = useCallback(async () => {
    await requestScreenWakeLock();
    setStreamPrepConfirmed(true);
    mediaPermissionAttemptedRef.current = true;
    await requestCameraAndMic();
  }, [requestCameraAndMic, requestScreenWakeLock]);

  useEffect(() => {
    if (!streamPrepConfirmed) return undefined;
    const restoreWakeLock = () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) {
        requestScreenWakeLock();
      }
    };
    document.addEventListener('visibilitychange', restoreWakeLock);
    return () => {
      document.removeEventListener('visibilitychange', restoreWakeLock);
      wakeLockRef.current?.release?.().catch(() => {});
      wakeLockRef.current = null;
    };
  }, [requestScreenWakeLock, streamPrepConfirmed]);

  // QR scanning starts one combined permission request. Returning devices whose
  // permissions are already saved continue without another prompt. Browsers
  // that require a fresh user gesture fall back to the single retry button.
  useEffect(() => {
    if (
      loading
      || error
      || !event
      || !livekitToken
      || !streamPrepConfirmed
      || mediaPermissionState !== 'idle'
      || mediaPermissionAttemptedRef.current
    ) return;
    mediaPermissionAttemptedRef.current = true;
    requestCameraAndMic();
  }, [error, event, livekitToken, loading, mediaPermissionState, requestCameraAndMic, streamPrepConfirmed]);

  const dispatchControlCommand = useCallback((message) => {
    try {
      const data = JSON.parse(message.data);
      const ownDeviceId = `${eventId}-device-${deviceNum}`;
      if (data.type === 'device_switch') {
        const active = data.active_device_id === ownDeviceId;
        window.dispatchEvent(new CustomEvent('pro-mode-device-active', {
          detail: { active, delayMs: data.audio_delay_ms }
        }));
      }
      if (data.type === 'switch_camera') window.dispatchEvent(new CustomEvent('pro-mode-switch-camera'));
      if (data.type === 'set_camera_enabled') {
        window.dispatchEvent(new CustomEvent('pro-mode-set-camera-enabled', { detail: { enabled: data.enabled !== false } }));
      }
      if (data.type === 'focus_camera') window.dispatchEvent(new CustomEvent('pro-mode-focus-camera'));
      if (data.type === 'set_zoom') {
        window.dispatchEvent(new CustomEvent('pro-mode-set-zoom', { detail: { zoom: data.zoom } }));
      }
      if (data.type === 'set_torch') {
        window.dispatchEvent(new CustomEvent('pro-mode-set-torch', { detail: { enabled: data.enabled === true } }));
      }
      if (data.type === 'audio_settings_update') {
        window.dispatchEvent(new CustomEvent('pro-mode-audio-settings', { detail: { settings: data.settings || {} } }));
      }
      if (data.type === 'end_stream') window.dispatchEvent(new CustomEvent('pro-mode-end-stream'));
    } catch (socketError) {
      console.warn('[CamDevice] ignored invalid control socket message:', socketError);
    }
  }, [deviceNum, eventId]);

  const wsBase = BACKEND_URL.replace(/\/api\/?$/, '').replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
  const controlSocketUrl = `${wsBase}/api/ws/pro-mode/device/${eventId}/${deviceNum}`;
  useResilientWebSocket({
    url: controlSocketUrl,
    enabled: Boolean(
      eventId
      && deviceNum
      && connectionToken
      && !loading
      && !error
      && mediaPermissionState === 'granted'
    ),
    onMessage: dispatchControlCommand,
  });

  // Register device and get LiveKit token
  useEffect(() => {
    const registerDevice = async () => {
      try {
        if (!connectionToken) {
          setError("Missing connection token. Please scan the QR code from the Control Panel.");
          setLoading(false);
          return;
        }
        
        // Get event details (public endpoint)
        const eventRes = await axiosInstance.get(`/events/${eventId}`);
        setEvent(eventRes.data);

        // Register this device using public endpoint with token
        const registerRes = await axiosInstance.post("/pro-mode/device/register-public", {
          event_id: eventId,
          device_number: deviceNum,
          device_name: `Camera ${deviceNum}`,
          connection_token: connectionToken
        });

        setLivekitToken(registerRes.data.livekit_token);
        setLivekitUrl(registerRes.data.livekit_url);
        setAudioMode(normalizeProAudioMode(registerRes.data.device?.audio_settings?.mode));
        setLoading(false);
      } catch (error) {
        console.error("Registration error:", error);
        setError(error.response?.data?.detail || "Failed to register device");
        setLoading(false);
      }
    };

    registerDevice();
  }, [eventId, deviceNum, connectionToken]);

  const handleStatusChange = useCallback((status) => {
    console.log("Camera status:", status);
  }, []);

  if (loading) {
    return (
      <div className="h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Camera className="w-16 h-16 text-gray-600 mx-auto mb-4 animate-pulse" />
          <p className="text-white text-lg">Connecting Camera {deviceNum}...</p>
          <p className="text-gray-400 text-sm mt-2">Please allow camera access when prompted</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen bg-black flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-white text-xl font-bold mb-2">Connection Failed</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (!streamPrepConfirmed) {
    return (
      <div className="flex h-screen items-center justify-center bg-black px-5">
        <div className="w-full max-w-md rounded-2xl border border-blue-500/25 bg-[#0b1020] p-6 text-center shadow-2xl">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-600/15 text-blue-300">
            <Moon className="h-8 w-8" />
          </div>
          <h1 className="text-xl font-bold text-white">Protect Your Stream</h1>
          <p className="mt-2 text-sm leading-6 text-gray-300">
            Turn on <strong>Do Not Disturb</strong> before connecting so calls and notifications do not interrupt this camera.
          </p>
          <div className="mt-4 space-y-2 rounded-xl border border-white/10 bg-black/25 p-4 text-left text-sm text-gray-400">
            <p><strong className="text-white">iPhone/iPad:</strong> Control Center → Focus → Do Not Disturb</p>
            <p><strong className="text-white">Android:</strong> Quick Settings → Do Not Disturb</p>
          </div>
          <Button
            type="button"
            onClick={confirmStreamPrep}
            className="mt-6 min-h-12 w-full bg-blue-600 font-bold text-white hover:bg-blue-700"
            data-testid="confirm-do-not-disturb-button"
          >
            DND Is On — Connect Camera &amp; Mic
          </Button>
          <p className="mt-4 text-xs leading-5 text-gray-500">
            ShowMeLive will also request permission to keep this screen awake while the camera is connected.
          </p>
        </div>
      </div>
    );
  }

  if (mediaPermissionState !== 'granted') {
    const requesting = mediaPermissionState === 'requesting' || mediaPermissionState === 'idle';
    return (
      <div className="flex h-screen items-center justify-center bg-black px-5">
        <div className="w-full max-w-md rounded-2xl border border-blue-500/25 bg-[#0b1020] p-6 text-center shadow-2xl">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center gap-1 rounded-full bg-blue-600/15 text-blue-300">
            <Camera className="h-7 w-7" />
            <Mic className="h-6 w-6" />
          </div>
          <h1 className="text-xl font-bold text-white">Connect Camera &amp; Mic</h1>
          <p className="mt-2 text-sm leading-6 text-gray-400">
            {requesting
              ? 'Allow camera and microphone access when your browser asks. Both are requested together.'
              : mediaPermissionError}
          </p>
          {requesting ? (
            <div className="mt-6 flex items-center justify-center gap-2 text-sm font-semibold text-blue-300">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-300/30 border-t-blue-300" />
              Waiting for permission
            </div>
          ) : (
            <Button
              type="button"
              onClick={requestCameraAndMic}
              className="mt-6 min-h-12 w-full bg-blue-600 font-bold text-white hover:bg-blue-700"
              data-testid="allow-camera-mic-button"
            >
              <Camera className="mr-2 h-5 w-5" />
              Allow Camera &amp; Mic
            </Button>
          )}
          <p className="mt-4 text-xs text-gray-500">Permission is saved by your browser according to your device settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="live-viewport-lock bg-black flex flex-col overflow-hidden"
      style={{ height: `${liveViewport.height}px`, width: `${liveViewport.width}px` }}
      data-live-viewport-locked="true"
    >
      {/* Connection Status Bar */}
      <div className={`flex items-center justify-between px-4 py-2 ${isConnected ? 'bg-green-900/50' : 'border-y border-blue-500/35 bg-blue-950/80'}`}>
        <div className="flex items-center gap-2">
          {isConnected ? (
            <Wifi className="w-4 h-4 text-green-400" />
          ) : (
            <WifiOff className="w-4 h-4 text-blue-400" />
          )}
          <span className={`text-sm ${isConnected ? 'text-green-400' : 'text-blue-300'}`}>
            {isConnected ? 'Connected to Control Panel' : 'Reconnecting...'}
          </span>
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
              connectionReadiness.level === 'limited'
                ? 'bg-amber-400/15 text-amber-300'
                : 'bg-blue-400/15 text-blue-200'
            }`}
            title={connectionReadiness.detail}
            data-testid="pro-camera-quality-profile"
          >
            {connectionReadiness.label} · RAW audio
          </span>
        </div>
        <span className="text-white text-sm">{event?.title}</span>
      </div>

      {/* Camera View */}
      <div className="flex-1 min-h-0">
        {livekitToken && livekitUrl ? (
          <LiveKitRoom
            key={`${deviceNum}-${liveKitRoomGeneration}`}
            serverUrl={livekitUrl}
            token={livekitToken}
            connect={true}
            video={getProVideoOptions(STREAM_ORIENTATION, 'user')}
            audio={false}
            onConnected={() => {
              cancelLiveKitRecovery();
              liveKitRecoveryAttemptRef.current = 0;
              setLiveKitConnected(true);
            }}
            onDisconnected={() => {
              setLiveKitConnected(false);
              scheduleLiveKitRecovery();
            }}
            onError={(connectionError) => {
              console.error('[CamDevice] LiveKit connection error:', connectionError);
              setLiveKitConnected(false);
              scheduleLiveKitRecovery();
            }}
            options={{
              adaptiveStream: true,
              dynacast: true,
              disconnectOnPageLeave: false,
              stopLocalTrackOnUnpublish: true,
              // Stable multi-camera publishing: 720p/30 with simulcast lets
              // LiveKit adapt layers without overloading each phone encoder.
              publishDefaults: getProPublishDefaults(videoProfile, audioMode),
              videoCaptureDefaults: getProVideoOptions(STREAM_ORIENTATION, 'user'),
              audioCaptureDefaults: getProAudioCaptureOptions(audioMode),
              reconnectPolicy: {
                nextRetryDelayInMs: (ctx) => Math.min(1000 * Math.pow(2, ctx.retryCount), 10000),
                maxRetries: 15,
              },
            }}
          >
            <CameraPublisher 
              eventId={eventId}
              deviceNumber={deviceNum}
              isActive={isActive}
              onActiveChange={setIsActive}
              onControlPanelPresenceChange={setControlPanelPresent}
              onStatusChange={handleStatusChange}
              orientation={STREAM_ORIENTATION}
              audioMode={audioMode}
              onAudioModeChange={setAudioMode}
              videoProfile={videoProfile}
            />
            <StreamDiagnostics label={`Camera ${deviceNum}`} />
          </LiveKitRoom>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <Camera className="w-16 h-16 text-gray-600 mx-auto mb-2" />
              <p className="text-gray-400">Initializing camera...</p>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Info */}
      <div className="bg-gray-900 px-4 py-3 text-center">
        <p className="text-gray-400 text-sm">
          Keep this page open while streaming. The control panel will manage your camera.
        </p>
      </div>
    </div>
  );
};

export default ProModeCameraDevice;
